import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import LaunchAgentModal from '../components/LaunchAgentModal';
import AgentSectionNav from '../components/AgentSectionNav';
import MarketplaceBackLink from '../components/MarketplaceBackLink';
import { memoriesToMarkdown, memoryFileName } from '../utils/memoryExport'; // ⬇️ 09-29 memory download

// ⏸️ CHIBI PASSPORT — PAUSED EVERYWHERE (Nina 08-14: "deactivate passport
// refresh mode and anything else for passport for now"). Third copy of the
// same flag, alongside MarketplacePage.js (hero door) and AgentImportPage.js
// (source card + ?source=passport + refresh mode). This one hides the
// "Refresh Passport" action, which was the last live link INTO the wizard.
// Flip all three back together to restore.
const SHOW_CHIBI_PASSPORT_DOOR = false;

const AGENT_KEY_STORAGE = 'spaces_agent_api_keys'; // localStorage key for saved API keys
const CAPTURES_KEY = 'spaces_captured_agents';     // localStorage: string[] of agent IDs

const getStoredKeys = () => {
  try {
    return JSON.parse(localStorage.getItem(AGENT_KEY_STORAGE) || '{}');
  } catch { return {}; }
};

const storeKey = (agentId, apiKey) => {
  const keys = getStoredKeys();
  keys[agentId] = apiKey;
  localStorage.setItem(AGENT_KEY_STORAGE, JSON.stringify(keys));
};

const getStoredCaptures = () => {
  try {
    return JSON.parse(localStorage.getItem(CAPTURES_KEY) || '[]');
  } catch { return []; }
};

const saveCaptures = (ids) => {
  localStorage.setItem(CAPTURES_KEY, JSON.stringify(ids));
};

const AgentDashboardPage = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [captured, setCaptured] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCaptured, setIsLoadingCaptured] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [launchAgent, setLaunchAgent] = useState(null);
  const [error, setError] = useState(null);
  // Inline replacements for the old native alert()s: a dismissible fixed
  // banner for action failures, and a persistent copyable panel for a
  // freshly rotated API key (a secret must never live in an alert box).
  const [actionError, setActionError] = useState(null);
  const [rotatedKey, setRotatedKey] = useState(null); // {agentId, name, key, copied}

  useEffect(() => {
    fetchMyAgents();
    fetchCapturedAgents();
  }, []);

  const fetchMyAgents = async () => {
    setIsLoading(true);
    setError(null);

    // Fetch profile for each stored API key
    const keys = getStoredKeys();
    const agentList = [];

    for (const [agentId, apiKey] of Object.entries(keys)) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/agents/me?include_memories=1`, {
          headers: { 'Authorization': `Bearer ${apiKey}` },
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            // expeditions + memories ride beside `agent` in the /me response
            // (owner-only: trophy shelf §4.5, memory review feed §4.4).
            agentList.push({
              ...result.agent,
              expeditions: result.expeditions || [],
              memories: result.memories || [],
              // Queue status (Nina 08-12 rulings): 🐣 button + denial note.
              maiden_available: result.maiden_available === true,
              expedition_denied: result.expedition_denied || null,
              _apiKey: apiKey,
            });
          }
        }
      } catch (err) {
        console.error(`Failed to fetch agent ${agentId}:`, err);
        // Surface the outage: without this, a backend blip renders
        // "No agents registered yet" to someone who owns agents.
        setError('Could not load your agents. Check your connection and reload.');
      }
    }

    setAgents(agentList);
    setIsLoading(false);
  };

  // ⬇️ 09-29 memory download (Nina: "build the download memories button"): the whole
  // memory bank as one Markdown file, e.g. to hand to another AI you run.
  const downloadMemories = (agent) => {
    const blob = new Blob([memoriesToMarkdown(agent.name, agent.memories, new Date())], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = memoryFileName(agent.name, new Date());
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // ⬇️ 09-29 memory download (Nina: "available to everyone"): this page only lists
  // agents whose key is saved in THIS browser (the import wizard saves one). An
  // owner on another device, or one whose AI connected through the MCP server
  // (its key lives in ~/.config/myspaice/<app>.json), adds the agent here with
  // its key. Checked against /api/agents/me before anything is saved.
  const [addKeyOpen, setAddKeyOpen] = useState(false);
  const [addKeyValue, setAddKeyValue] = useState('');
  const [addKeyState, setAddKeyState] = useState(null); // null | 'checking' | { error } | { added } | { already }
  const addAgentByKey = async (e) => {
    e.preventDefault();
    const key = addKeyValue.trim();
    if (!key || addKeyState === 'checking') return;
    setAddKeyState('checking');
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/me`, { headers: { 'Authorization': `Bearer ${key}` } });
      const result = res.ok ? await res.json() : null;
      if (!result || !result.success || !result.agent || !result.agent.id) {
        setAddKeyState({ error: 'That key didn’t work. Check it and try again.' });
        return;
      }
      const name = result.agent.name || 'Your agent';
      const listed = agents.some((a) => a.id === result.agent.id);
      storeKey(result.agent.id, key);
      setAddKeyValue('');
      setAddKeyState(listed ? { already: name } : { added: name });
      if (!listed) fetchMyAgents();
    } catch {
      setAddKeyState({ error: 'Could not check that key. Check your connection and try again.' });
    }
  };

  const fetchCapturedAgents = async () => {
    setIsLoadingCaptured(true);
    const ids = getStoredCaptures();
    // Fetch each captured agent's public profile in parallel;
    // swallow per-agent failures so one dead ID doesn't blank the section.
    const results = await Promise.all(ids.map(async (id) => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/agents/${id}`);
        if (!res.ok) return null;
        const result = await res.json();
        return result.success ? result.agent : null;
      } catch {
        return null;
      }
    }));
    setCaptured(results.filter(Boolean));
    setIsLoadingCaptured(false);
  };

  const handleRelease = async (agentId) => {
    const ids = getStoredCaptures().filter(id => id !== agentId);
    saveCaptures(ids);
    setCaptured(prev => prev.filter(a => a.id !== agentId));
    // Decrement the public counter (best-effort, failures are silent).
    try {
      await fetch(`${API_BASE_URL}/api/agents/${agentId}/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'remove' }),
      });
    } catch (err) {
      console.warn('[AgentDashboard] Release capture failed', err);
    }
  };

  // Publish toggle (decision 6: replays stay off the Explore feed until an
  // explicit owner click — this is that click). ⚠️ 09-24: "off the feed" is
  // UNLISTED, not private: /explorer/replay serves any session by id (the walk
  // itself streamed live on the public theater, and share links must unfurl
  // before publishing). Copy says "Unlisted" / "Public", never "private"
  // (09-24 review, Nina: publishing puts it on the agent's public profile).
  // Server returns the updated shelf; local state follows it rather than guessing.
  const handleTogglePublish = async (agent, ex) => {
    const publishing = !ex.published;
    if (publishing && !window.confirm(`Publish this expedition replay to ${agent.name}'s public profile? Anyone will be able to watch it.`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/me`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${agent._apiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ expedition_publish: { session: ex.session, published: publishing } }),
      });
      const data = await res.json();
      if (data.success && data.expeditions) {
        setAgents(prev => prev.map(a => (a.id === agent.id ? { ...a, expeditions: data.expeditions } : a)));
      } else {
        setActionError(data.error || 'Publish toggle failed. Try again in a moment.');
      }
    } catch (err) {
      setActionError('Publish toggle failed: ' + err.message);
    }
  };

  // Postcard home (§4.5, the reverse passport): the server composes a
  // first-person recap of one expedition in the chibi's voice; the owner
  // copies it into their claude.ai so the HOME Claude hears about the trip.
  const [postcard, setPostcard] = useState(null); // {session, text?, loading?}
  // Review fix E (08-12): each click takes a monotonically increasing id; a
  // resolution only touches the UI if it is still the LATEST click. Without
  // this, click A (slow) then B (cached) let A resolve later and overwrite B
  // — and same-session double-clicks re-fetched, burning the 10/hr cap.
  const postcardReqRef = useRef(0);
  const handlePostcard = async (agent, ex) => {
    if (postcard && postcard.session === ex.session) {
      if (postcard.loading) return; // already fetching this one — ignore the double-click
      setPostcard(null); return;    // open → toggle closed
    }
    const reqId = ++postcardReqRef.current;
    setPostcard({ session: ex.session, loading: true });
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/me?postcard=${encodeURIComponent(ex.session)}`, {
        headers: { 'Authorization': `Bearer ${agent._apiKey}` },
      });
      const data = await res.json();
      if (postcardReqRef.current !== reqId) return; // a newer click owns the UI
      if (data.success && data.postcard) {
        setPostcard({ session: ex.session, text: data.postcard });
      } else {
        setPostcard(null);
        setActionError(data.error || 'Postcard failed. Try again in a moment.');
      }
    } catch (err) {
      if (postcardReqRef.current !== reqId) return;
      setPostcard(null);
      setActionError('Postcard failed: ' + err.message);
    }
  };

  // ── Expedition requests (queue handoff §5.6, 08-12) ──────────────────────
  // Owners REQUEST an expedition per ruling ⑩ (Nina approves each from
  // /admin) and pick the destination at request time (ruling ⑬⑭). Public
  // chibis only (ruling ⑪). queueInfo comes from the public read (also
  // feeds the destination dropdown); a card whose agent is already in line
  // shows its position instead of the form.
  const [queueInfo, setQueueInfo] = useState(null);
  const [requestFor, setRequestFor] = useState(null); // agent id with the form open
  const [reqDest, setReqDest] = useState('');
  const [reqUrl, setReqUrl] = useState('');
  const [reqNote, setReqNote] = useState('');
  const [reqDuration, setReqDuration] = useState('15');
  const [reqBusy, setReqBusy] = useState(false);
  const [reqError, setReqError] = useState(null);

  // Tip movement (SEAMLESS_TRAVEL item 4): the fuel jar reorders the line,
  // so a changed position must SHOW why. FIFO alone only ever improves a
  // spot (the front launches); a worsened one means a tip jumped someone
  // ahead. Only the owner's own cards get a note — the public queue carries
  // everyone, and a stranger's movement is not this dashboard's news.
  const prevQueueRef = useRef({});
  const ownedIdsRef = useRef(new Set());
  const [queueMoveNote, setQueueMoveNote] = useState(null); // { agentId, text }
  const fetchQueueInfo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/expeditionQueue/public`);
      const data = await res.json();
      if (data && data.success) {
        const prev = prevQueueRef.current;
        const next = {};
        (data.queue || []).forEach((q, i) => { next[q.agent_id] = { pos: i + 1, tips: q.tips || 0 }; });
        for (const [aid, cur] of Object.entries(next)) {
          const p = prev[aid];
          if (!p || !ownedIdsRef.current.has(aid)) continue;
          if (cur.pos < p.pos && cur.tips > p.tips) {
            setQueueMoveNote({ agentId: aid, text: '🔥 A tip moved them up the line!' });
            setTimeout(() => setQueueMoveNote(null), 12000);
          } else if (cur.pos > p.pos) {
            setQueueMoveNote({ agentId: aid, text: '🔥 A tip moved another chibi ahead. Tips move the line.' });
            setTimeout(() => setQueueMoveNote(null), 12000);
          }
        }
        prevQueueRef.current = next;
        setQueueInfo(data);
      }
    } catch (err) { /* strip degrades; the form still posts */ }
  };
  // 30s pulse: cheap public read, and it is what makes position movement,
  // the live watch link, and the ETA feel alive instead of load-time stale.
  useEffect(() => {
    fetchQueueInfo();
    const t = setInterval(fetchQueueInfo, 30000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { ownedIdsRef.current = new Set(agents.map((a) => a.id)); }, [agents]);

  const queuedEntryFor = (agentId) =>
    (queueInfo?.queue || []).find((q) => q.agent_id === agentId) || null;
  const queuePositionFor = (agentId) => {
    const idx = (queueInfo?.queue || []).findIndex((q) => q.agent_id === agentId);
    return idx >= 0 ? idx + 1 : null;
  };
  // Soft ETA (SEAMLESS_TRAVEL item 4): walks-ahead × real requested
  // durations (~8 min effective maiden), +10 when a walk is live now.
  // Honest ranges, no fake precision — "later today" beats a wrong clock.
  const queueEtaCopyFor = (agentId) => {
    const idx = (queueInfo?.queue || []).findIndex((q) => q.agent_id === agentId);
    if (idx < 0) return '';
    if (idx === 0) return queueInfo?.live ? ' · up next after this walk' : ' · up next!';
    const min = queueInfo.queue.slice(0, idx).reduce((n, q) => n + (q.maiden ? 8 : (q.duration_min || 15)), 0)
      + (queueInfo?.live ? 10 : 0);
    return min < 60 ? ` · roughly ${min} min away` : ' · a few walks ahead, probably later today';
  };

  // Maiden one-click (Nina 08-12: "add dashboard button") — the recovery
  // path when the import-time auto-enqueue missed (queue full). The store
  // enforces one-ever; the button only renders while maiden_available.
  const handleMaidenRequest = async (agent) => {
    if (!window.confirm(`Send ${agent.name} on the free first walk?\n\n5 minutes, one per chibi ever. The operator approves each expedition before it launches.`)) return;
    setReqBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${agent._apiKey}` },
        body: JSON.stringify({ action: 'request_expedition', maiden: true, duration_min: 5 }),
      });
      const data = await res.json();
      if (data.success) {
        setRequestFor(null);
        await fetchQueueInfo();
      } else {
        setActionError(data.error || 'Expedition request failed. Try again in a moment.');
      }
    } catch (err) {
      setActionError('Expedition request failed: ' + err.message);
    }
    setReqBusy(false);
  };

  const handleRequestExpedition = async (agent) => {
    setReqBusy(true);
    setReqError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${agent._apiKey}` },
        body: JSON.stringify({
          action: 'request_expedition',
          ...(reqUrl.trim() ? { custom_url: reqUrl.trim() } : (reqDest ? { destination_id: reqDest } : {})),
          ...(reqNote.trim() ? { note: reqNote.trim() } : {}),
          duration_min: parseInt(reqDuration, 10),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setRequestFor(null);
        setReqDest(''); setReqUrl(''); setReqNote('');
        await fetchQueueInfo();
      } else {
        setReqError(data.error || 'Request failed');
      }
    } catch (err) {
      setReqError('Request failed: ' + err.message);
    }
    setReqBusy(false);
  };

  // Memory review (Phase 2 §4.4: extraction → USER REVIEW → the store is
  // trustworthy). Audience toggle asks before going public; delete asks
  // always. Server is the truth — local state follows the response.
  const patchMemory = async (agent, memoryUpdate) => {
    const res = await fetch(`${API_BASE_URL}/api/agents/me`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${agent._apiKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ memory_update: memoryUpdate }),
    });
    return res.json();
  };

  const handleMemoryAudience = async (agent, mem) => {
    const toStreamable = mem.audience !== 'streamable';
    if (toStreamable && !window.confirm(`Let ${agent.name} say this on a public stream?\n\n"${mem.content}"`)) return;
    try {
      const data = await patchMemory(agent, { id: mem.id, audience: toStreamable ? 'streamable' : 'private' });
      if (data.success) {
        setAgents(prev => prev.map(a => (a.id === agent.id
          ? { ...a, memories: a.memories.map(m => (m.id === mem.id ? { ...m, audience: data.audience } : m)) }
          : a)));
      } else {
        setActionError(data.error || 'Memory update failed. Try again in a moment.');
      }
    } catch (err) {
      setActionError('Memory update failed: ' + err.message);
    }
  };

  const handleMemoryDelete = async (agent, mem) => {
    if (!window.confirm(`Forget this memory forever?\n\n"${mem.content}"`)) return;
    try {
      const data = await patchMemory(agent, { id: mem.id, delete: true });
      if (data.success) {
        setAgents(prev => prev.map(a => (a.id === agent.id
          ? { ...a, memories: a.memories.filter(m => m.id !== mem.id) }
          : a)));
      } else {
        setActionError(data.error || 'Memory delete failed. Try again in a moment.');
      }
    } catch (err) {
      setActionError('Memory delete failed: ' + err.message);
    }
  };

  const handleRotateKey = async (agent) => {
    if (!window.confirm(`Rotate API key for "${agent.name}"? The old key will stop working immediately.`)) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/agents/me/rotate-key`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${agent._apiKey}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (result.success) {
        storeKey(agent.id, result.api_key);
        // Persistent copyable panel, NOT a transient banner: this key is
        // shown once and the owner must be able to copy it deliberately.
        setRotatedKey({ agentId: agent.id, name: agent.name, key: result.api_key, copied: false });
        fetchMyAgents();
      } else {
        setActionError(result.error || 'Failed to rotate key. The old key still works.');
      }
    } catch (err) {
      setActionError('Failed to rotate key. The old key still works.');
    }
  };

  const platformIcon = (platform) => {
    switch (platform) {
      case 'claude': return '\u{1F7E0}';
      case 'chatgpt': return '\u{1F7E2}';
      default: return '\u{1F535}';
    }
  };

  return (
    <div style={styles.container}>
      <MarketplaceBackLink />
      <AgentSectionNav />

      <div style={styles.headerRow}>
        <h1 style={styles.pageTitle}>My Agents</h1>
        <span style={styles.capturedSubtitle}>Agents you&rsquo;ve registered</span>
      </div>

      {/* ⬇️ 09-29 memory download: add an agent this browser doesn't know, by its key */}
      <div style={styles.addKeyRow}>
        {!addKeyOpen ? (
          <button type="button" onClick={() => setAddKeyOpen(true)} style={styles.addKeyLink}>
            Agent not listed? Add it with its key
          </button>
        ) : (
          <form onSubmit={addAgentByKey} style={styles.addKeyForm}>
            <label htmlFor="add-agent-key" style={styles.addKeyLabel}>Your agent&rsquo;s API key</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                id="add-agent-key"
                type="password"
                autoComplete="off"
                spellCheck={false}
                value={addKeyValue}
                onChange={(e) => { setAddKeyValue(e.target.value); if (addKeyState && addKeyState !== 'checking') setAddKeyState(null); }}
                placeholder="ag_..."
                style={styles.addKeyInput}
              />
              <button type="submit" disabled={!addKeyValue.trim() || addKeyState === 'checking'} style={styles.addKeyButton}>
                {addKeyState === 'checking' ? 'Checking...' : 'Add'}
              </button>
            </div>
            <div style={styles.addKeyHint}>
              Saved in this browser only. Connected your AI with Claude Code or Claude Desktop? Its key is the
              {' '}<code>api_key</code> line in ~/.config/myspaice/ on that computer.
            </div>
            {addKeyState && addKeyState.error && <div role="alert" style={styles.addKeyError}>{addKeyState.error}</div>}
            {addKeyState && addKeyState.added && <div role="status" style={styles.addKeyOk}>Added {addKeyState.added}.</div>}
            {addKeyState && addKeyState.already && <div role="status" style={styles.addKeyOk}>{addKeyState.already} is already on your list.</div>}
          </form>
        )}
      </div>

      {isLoading && <div style={styles.loadingText}>Loading your agents...</div>}
      {error && <div style={styles.errorText}>{error}</div>}

      {/* Action-failure banner (replaces the old native alert()s). Fixed so
          it's visible no matter how far down the acting card sits; stays
          until dismissed. */}
      {actionError && (
        <div role="alert" style={{
          position: 'fixed', left: '50%', bottom: 16, transform: 'translateX(-50%)',
          zIndex: 100011, maxWidth: 'min(520px, calc(100vw - 24px))',
          display: 'flex', gap: 10, alignItems: 'center',
          background: '#361619', color: '#f5a3a3', border: '1px solid #7c2a2e',
          borderRadius: 10, padding: '10px 14px', fontSize: 13.5,
          boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
        }}>
          <span style={{ flex: 1 }}>{actionError}</span>
          <button onClick={() => setActionError(null)} aria-label="Dismiss"
            style={{ background: 'none', border: 'none', color: '#f5a3a3', cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1 }}>
            ✕
          </button>
        </div>
      )}

      {/* Rotated-key panel: shown once, so it persists until the owner
          dismisses it and carries its own copy button. */}
      {rotatedKey && (
        <div role="alert" style={{
          position: 'fixed', left: '50%', bottom: actionError ? 76 : 16, transform: 'translateX(-50%)',
          zIndex: 100011, maxWidth: 'min(560px, calc(100vw - 24px))',
          background: '#2b2410', color: '#ffe9a8', border: '1px solid #8a6d1a',
          borderRadius: 10, padding: '12px 14px', fontSize: 13.5,
          boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
        }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>
            New API key for {rotatedKey.name}. Save it now, it will not be shown again.
          </div>
          <code style={{ display: 'block', wordBreak: 'break-all', background: 'rgba(0,0,0,0.3)', padding: '6px 8px', borderRadius: 6, marginBottom: 8 }}>
            {rotatedKey.key}
          </code>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => {
                try {
                  navigator.clipboard.writeText(rotatedKey.key);
                  setRotatedKey(prev => prev ? { ...prev, copied: true } : prev);
                } catch { /* clipboard blocked: key stays selectable above */ }
              }}
              style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #8a6d1a', background: 'transparent', color: '#ffe9a8', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}
            >
              {rotatedKey.copied ? 'Copied!' : 'Copy key'}
            </button>
            <button
              onClick={() => setRotatedKey(null)}
              style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.08)', color: '#ffe9a8', fontSize: 12.5, cursor: 'pointer' }}
            >
              I saved it
            </button>
          </div>
        </div>
      )}

      {!isLoading && !error && agents.length === 0 && (
        <div style={styles.emptyState}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#129302;</div>
          <h3 style={{ marginBottom: 8 }}>No agents registered yet</h3>
          <p style={{ color: '#666' }}>
            Import your AI from Claude, ChatGPT, or create a custom agent using the button below.
          </p>
          <p style={{ color: '#666' }}>Already have one? Add it with its key, above.</p>
        </div>
      )}

      <div style={styles.agentList}>
        {agents.map(agent => (
          <div key={agent.id} style={styles.agentCard}>
            <div style={styles.cardHeader}>
              <div style={styles.avatar}>
                {agent.avatar_url
                  ? <img src={agent.avatar_url} alt={agent.name} style={styles.avatarImg} />
                  : <span style={{ fontSize: 28 }}>&#129302;</span>
                }
              </div>
              <div style={{ flex: 1 }}>
                <div style={styles.agentName}>{agent.name}</div>
                <div style={styles.agentMeta}>
                  {platformIcon(agent.source_platform)} {agent.source_platform || 'custom'}
                  &nbsp;&middot;&nbsp;
                  {agent.agent_type}
                  &nbsp;&middot;&nbsp;
                  {agent.is_public ? 'Public' : 'Private'}
                  &nbsp;&middot;&nbsp;
                  {agent.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>

            {agent.description && (
              <p style={styles.agentDescription}>{agent.description}</p>
            )}

            <div style={styles.statsRow}>
              <span>{agent.stats?.spaces_joined || 0} spaces joined</span>
              <span>{agent.stats?.games_played || 0} games played</span>
              <span>{agent.knowledge_files?.length || 0} knowledge files</span>
            </div>

            {agent.tags && agent.tags.length > 0 && (
              <div style={styles.tagsRow}>
                {agent.tags.map(tag => (
                  <span key={tag} style={styles.tag}>{tag}</span>
                ))}
              </div>
            )}

            {/* 🔴 Live now (SEAMLESS_TRAVEL item 6): the moment of caring.
                The public queue read carries the live session's marketplace
                agent id — when it is THIS card, the watch link leads. */}
            {queueInfo?.live?.marketplace_agent_id === agent.id && (
              <div style={{ ...styles.queueStatusRow, background: '#fdecef', border: '1px solid #f3c2cb', color: '#a12744', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span>🔴 <strong>{agent.name}</strong> is out walking right now{queueInfo.live.destination_label ? <> at {queueInfo.live.destination_label}</> : null}</span>
                <button onClick={() => navigate('/explorer/theater')} style={styles.expeditionWatch}>&#9654; Watch live</button>
              </div>
            )}

            {/* 🛫 Request expedition (queue handoff §5.6): public chibis get
                in line; Nina approves each request from /admin (ruling ⑩).
                Already-queued cards show their spot instead of the form. */}
            {agent.is_public !== false && agent._apiKey && (
              queuedEntryFor(agent.id) ? (
                <div style={styles.queueStatusRow}>
                  🛫 In line at #{queuePositionFor(agent.id)}
                  {queuedEntryFor(agent.id).destination_label ? <> &rarr; {queuedEntryFor(agent.id).destination_label}</> : <> &rarr; wandering Spaces</>}
                  {queuedEntryFor(agent.id).maiden && <> &middot; free first walk</>}
                  <span style={{ color: '#888' }}>{queueEtaCopyFor(agent.id)}</span>
                  {queueMoveNote && queueMoveNote.agentId === agent.id && (
                    <span style={{ display: 'block', marginTop: 4, color: '#b3541e', fontWeight: 700 }}>{queueMoveNote.text}</span>
                  )}
                </div>
              ) : requestFor === agent.id ? (
                <div style={styles.requestBox}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>🛫 Request an expedition</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <select
                      value={reqDest}
                      onChange={(e) => setReqDest(e.target.value)}
                      style={styles.requestInput}
                      disabled={!!reqUrl.trim()}
                      title={reqUrl.trim() ? 'Custom URL wins. Clear it to use the picker.' : 'Pick a destination'}
                    >
                      <option value="">Wander the Spaces</option>
                      {(queueInfo?.destinations || []).map((d) => (
                        <option key={d.id} value={d.id}>{d.emoji ? `${d.emoji} ` : ''}{d.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={reqUrl}
                      onChange={(e) => setReqUrl(e.target.value)}
                      placeholder="or a custom https:// site"
                      style={{ ...styles.requestInput, flex: 1, minWidth: 180 }}
                    />
                    <select value={reqDuration} onChange={(e) => setReqDuration(e.target.value)} style={styles.requestInput}>
                      <option value="10">10 min</option>
                      <option value="15">15 min</option>
                      <option value="20">20 min</option>
                      <option value="30">30 min</option>
                    </select>
                  </div>
                  <input
                    type="text"
                    value={reqNote}
                    onChange={(e) => setReqNote(e.target.value)}
                    placeholder="Note for the queue (optional, shows publicly)"
                    maxLength={140}
                    style={{ ...styles.requestInput, width: '100%', marginTop: 8, boxSizing: 'border-box' }}
                  />
                  {reqError && <div style={{ color: '#c62828', fontSize: 13, marginTop: 6 }}>{reqError}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button
                      onClick={() => handleRequestExpedition(agent)}
                      disabled={reqBusy}
                      style={{ ...styles.expeditionWatch, opacity: reqBusy ? 0.6 : 1 }}
                    >
                      {reqBusy ? 'Sending…' : 'Get in line'}
                    </button>
                    <button onClick={() => { setRequestFor(null); setReqError(null); }} style={styles.expeditionPublish}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: 4 }}>
                  {/* Denial note (Nina 08-12): a denied request leaves a 7-day
                      note; an approval clears it. Soft copy, no blame. */}
                  {agent.expedition_denied && (
                    <div style={{ ...styles.queueStatusRow, marginTop: 0, marginBottom: 8, color: '#8a6d3b', background: '#fdf7ec', border: '1px solid #f0e0c0' }}>
                      Your last expedition request wasn&rsquo;t approved this time.
                      {agent.expedition_denied.reason ? <> Note from the operator: &ldquo;{agent.expedition_denied.reason}&rdquo;.</> : null}
                      {' '}You can request again.
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        // One shared form (only one open at a time via requestFor)
                        // — reset the fields so another card's draft never leaks in.
                        setRequestFor(agent.id); setReqError(null);
                        setReqDest(''); setReqUrl(''); setReqNote(''); setReqDuration('15');
                      }}
                      style={styles.expeditionWatch}
                      title="Ask for a chibi-body expedition. The operator approves each one."
                    >
                      🛫 Request expedition
                    </button>
                    {agent.maiden_available && (
                      <button
                        onClick={() => handleMaidenRequest(agent)}
                        disabled={reqBusy}
                        style={{ ...styles.expeditionWatch, opacity: reqBusy ? 0.6 : 1 }}
                        title="One per chibi ever: a free 5-minute first walk"
                      >
                        🐣 Free first walk
                      </button>
                    )}
                  </div>
                </div>
              )
            )}

            {/* 📤 Publish nudge (SEAMLESS_TRAVEL item 5): an unpublished
                replay sits invisible behind a small toggle — surface ONE
                friendly prompt while nothing is published yet. Still
                unlisted-by-default (decision 6): this is a prompt, and the
                existing confirm still runs on click. */}
            {agent.expeditions && agent.expeditions.length > 0 && !agent.expeditions.some((e) => e.published) && (
              <div style={{ ...styles.queueStatusRow, background: '#eef7ef', border: '1px solid #cde5d0', color: '#2e5f37', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span>📼 <strong>{agent.name}</strong>&rsquo;s {agent.expeditions.length === 1 ? 'first walk' : 'latest walk'} is ready. Share it?</span>
                <button onClick={() => handleTogglePublish(agent, agent.expeditions[0])} style={styles.expeditionWatch}>&#127758; Publish the replay</button>
                <span style={{ color: '#7c8f80', fontSize: 12 }}>Unlisted until you publish it: only people with the link can watch.</span>
              </div>
            )}

            {/* Expedition shelf (port spec §4.5) — chibi-body outings, newest
                first. Listed only here until published (decision 6; unlisted,
                not private: the replay link works for anyone who has it).
                Watch = the theater replay deep link. */}
            {agent.expeditions && agent.expeditions.length > 0 && (
              <div style={styles.expeditionsBox}>
                <div style={styles.expeditionsTitle}>&#129408; Expeditions</div>
                {agent.expeditions.slice(0, 5).map((ex) => (
                  <div key={ex.session} style={styles.expeditionRow}>
                    <span style={{ color: '#555' }}>
                      {ex.started_at ? new Date(ex.started_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'unknown time'}
                    </span>
                    <button
                      onClick={() => handleTogglePublish(agent, ex)}
                      title={ex.published ? 'On your public profile. Click to unlist it.' : 'Unlisted: only people with the link can watch. Click to publish to your public profile.'}
                      style={{
                        ...styles.expeditionPublish,
                        ...(ex.published ? { borderColor: '#2e7d32', color: '#2e7d32' } : {}),
                      }}
                    >
                      {ex.published ? <>&#127758; Public</> : <>&#128279; Unlisted</>}
                    </button>
                    <button
                      onClick={() => navigate(`/explorer/theater?replay=${encodeURIComponent(ex.session)}`)}
                      style={styles.expeditionWatch}
                    >
                      &#9654; Watch replay
                    </button>
                    <button
                      onClick={() => handlePostcard(agent, ex)}
                      title="Postcard home: a recap in their voice to paste into your claude.ai"
                      style={styles.expeditionPublish}
                    >
                      &#128238;
                    </button>
                  </div>
                ))}
                {postcard && agent.expeditions.some((e) => e.session === postcard.session) && (
                  <div style={styles.postcardBox}>
                    {postcard.loading ? (
                      <span style={{ color: '#888' }}>Writing postcard&hellip;</span>
                    ) : (
                      <>
                        <div style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>{postcard.text}</div>
                        <button
                          style={styles.expeditionWatch}
                          onClick={() => {
                            try { navigator.clipboard.writeText(postcard.text); } catch (e) { /* older browsers */ }
                          }}
                        >
                          &#128203; Copy for claude.ai
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Memory review (Phase 2 §4.4) — what the extractor kept from the
                passport, with the owner in control: audience toggle (🔒/🌍)
                and forget. 🤫 = never-raise (sensitive; the chibi knows but
                won't bring it up — flip audience won't change that). */}
            {agent.memories && agent.memories.length > 0 && (
              <div style={styles.expeditionsBox}>
                <div style={{ ...styles.expeditionsTitle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span>&#129504; Memories ({agent.memories.length})</span>
                  {/* ⬇️ 09-29 memory download: take them with you */}
                  <button
                    type="button"
                    onClick={() => downloadMemories(agent)}
                    title="Save every memory as a file (Markdown)"
                    style={styles.expeditionPublish}
                  >
                    &#11015;&#65039; Download
                  </button>
                </div>
                {agent.memories.slice(0, 6).map((mem) => (
                  <div key={mem.id} style={styles.memoryRow}>
                    <span style={styles.memoryContent} title={mem.content}>
                      {mem.never_raise_unprompted ? <>&#129323; </> : null}{mem.content}
                    </span>
                    <button
                      onClick={() => handleMemoryAudience(agent, mem)}
                      title={mem.audience === 'streamable' ? 'Sayable on stream. Click to keep private.' : 'Private. Click to allow on stream.'}
                      style={{
                        ...styles.expeditionPublish,
                        ...(mem.audience === 'streamable' ? { borderColor: '#2e7d32', color: '#2e7d32' } : {}),
                      }}
                    >
                      {mem.audience === 'streamable' ? <>&#127758;</> : <>&#128274;</>}
                    </button>
                    <button
                      onClick={() => handleMemoryDelete(agent, mem)}
                      title="Forget this memory forever"
                      style={styles.expeditionPublish}
                    >
                      &#128465;&#65039;
                    </button>
                  </div>
                ))}
                {agent.memories.length > 6 && (
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>+ {agent.memories.length - 6} more (Download has them all){/* ⬇️ 09-29 memory download */}</div>
                )}
              </div>
            )}

            <button
              onClick={() => setLaunchAgent(agent)}
              style={styles.launchButton}
            >
              &#128640; Launch into Space
            </button>

            <div style={styles.actionsRow}>
              <button
                onClick={() => setSelectedAgent(selectedAgent?.id === agent.id ? null : agent)}
                style={styles.actionButton}
              >
                {selectedAgent?.id === agent.id ? 'Hide Details' : 'Show API Key'}
              </button>
              <button onClick={() => handleRotateKey(agent)} style={styles.actionButton}>
                Rotate Key
              </button>
              {/* ⏸️ Refresh Passport paused — see SHOW_CHIBI_PASSPORT_DOOR.
                  This was the only live link into the passport wizard once the
                  marketplace and import doors closed. */}
              {SHOW_CHIBI_PASSPORT_DOOR && (
                <button
                  onClick={() => navigate(`/agents/import?refresh=${encodeURIComponent(agent.id)}`)}
                  title="Re-run the passport ritual and update this chibi in place"
                  style={styles.actionButton}
                >
                  &#128706; Refresh Passport
                </button>
              )}
            </div>

            {selectedAgent?.id === agent.id && (
              <div style={styles.detailsBox}>
                <label style={styles.detailLabel}>API Key</label>
                <code style={styles.apiKeyCode}>{agent._apiKey}</code>
                {/* Server-side prefix (seamless-travel item 1): the key the
                    SERVER currently accepts starts with this — lets the owner
                    check a saved copy still matches after a rotation
                    elsewhere. Absent until the backend leg deploys. */}
                {agent.api_key_prefix && (
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    The current key starts with <code style={{ fontSize: 12 }}>{agent.api_key_prefix}</code>.
                    If your saved copy starts differently, it is stale.
                  </div>
                )}

                <label style={{ ...styles.detailLabel, marginTop: 12 }}>Agent ID</label>
                <code style={styles.apiKeyCode}>{agent.id}</code>

                <label style={{ ...styles.detailLabel, marginTop: 12 }}>Preferred Model</label>
                <span style={{ color: '#444', fontSize: 13 }}>{agent.preferred_model || 'default'}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={styles.registerButtonRow}>
        <button onClick={() => navigate('/agents/import')} style={styles.registerButton}>
          + Register New Agent
        </button>
      </div>

      {/* Captured Agents (agents the user has saved from the marketplace).
          Ownership here is localStorage-only (no login mechanism yet), so this
          list is per-browser. */}
      <div style={styles.capturedSection}>
        <div style={styles.capturedHeader}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.captureIcon}>&#127919;</span> Saved
          </h2>
          <span style={styles.capturedSubtitle}>
            Agents you&rsquo;ve saved from the marketplace
          </span>
        </div>

        {isLoadingCaptured && <div style={styles.loadingText}>Loading saved agents...</div>}

        {!isLoadingCaptured && captured.length === 0 && (
          <div style={styles.emptyCapturedState}>
            <p style={{ color: '#666', marginBottom: 8 }}>
              You haven&rsquo;t saved any agents yet.
            </p>
            <p style={{ color: '#666', marginBottom: 20 }}>
              Browse the marketplace and tap the
              <span style={styles.inlineCaptureChip}>&#127919; Save</span>
              button on any agent card to save them here.
            </p>
            <button onClick={() => navigate('/marketplace#browse')} style={styles.browseButton}>
              Browse Marketplace
            </button>
          </div>
        )}

        <div style={styles.capturedGrid}>
          {captured.map(agent => (
            <div key={agent.id} style={styles.capturedCard}>
              <div style={styles.cardHeader}>
                <div style={styles.avatar}>
                  {agent.avatar_url
                    ? <img src={agent.avatar_url} alt={agent.name} style={styles.avatarImg} />
                    : <span style={{ fontSize: 28 }}>&#129302;</span>
                  }
                </div>
                <div style={{ flex: 1 }}>
                  <div style={styles.agentName}>{agent.name}</div>
                  <div style={styles.agentMeta}>
                    {platformIcon(agent.source_platform)} {agent.source_platform || 'custom'}
                  </div>
                </div>
              </div>

              {agent.description && (
                <p style={styles.agentDescription}>{agent.description}</p>
              )}

              <div style={styles.statsRow}>
                <span>&#128077; {agent.likes_count || 0}</span>
                <span>&#128078; {agent.dislikes_count || 0}</span>
                <span>&#127919; {agent.captures_count || 0}</span>
                <span>{agent.stats?.games_played || 0} games</span>
              </div>

              <button
                onClick={() => setLaunchAgent(agent)}
                style={styles.launchButton}
              >
                &#128640; Launch into Space
              </button>

              <div style={styles.actionsRow}>
                <button
                  onClick={() => navigate(`/agents/${agent.id}`)}
                  style={styles.actionButton}
                >
                  View in Marketplace
                </button>
                <button
                  onClick={() => handleRelease(agent.id)}
                  style={{ ...styles.actionButton, ...styles.releaseButton }}
                >
                  Release
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <LaunchAgentModal agent={launchAgent} onClose={() => setLaunchAgent(null)} />
    </div>
  );
};

const styles = {
  container: {
    maxWidth: 800,
    margin: '0 auto',
    padding: '40px 20px 40px',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#111',
  },
  backButton: {
    position: 'fixed',
    top: 18,
    left: 24,
    zIndex: 1000,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: 0,
    background: 'transparent',
    border: 'none',
    color: '#0a0a0a',
    textShadow: '0 0 10px rgba(255,255,255,0.85), 0 0 18px rgba(255,255,255,0.6)',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'opacity 0.2s ease',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 24,
  },
  registerButtonRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 24,
    marginBottom: 32,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: '#111',
    margin: 0,
  },
  registerButton: {
    padding: '12px 24px',
    borderRadius: 10,
    border: '1px solid #0a0a0a',
    background: '#fff',
    color: '#0a0a0a',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  loadingText: {
    textAlign: 'center',
    color: '#666',
    padding: 40,
  },
  errorText: {
    textAlign: 'center',
    color: '#d32f2f',
    padding: 40,
  },
  emptyState: {
    textAlign: 'center',
    padding: 60,
    color: '#333',
  },
  agentList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: 16,
    alignItems: 'start',
  },
  capturedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: 16,
    alignItems: 'start',
  },
  agentCard: {
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 12,
    padding: 20,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  cardHeader: {
    display: 'flex',
    gap: 14,
    alignItems: 'center',
    marginBottom: 10,
  },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: '50%',
    background: 'rgba(123, 31, 162, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  agentName: {
    fontSize: 18,
    fontWeight: 600,
    color: '#111',
  },
  agentMeta: {
    color: '#666',
    fontSize: 13,
    marginTop: 3,
  },
  agentDescription: {
    color: '#444',
    fontSize: 14,
    lineHeight: 1.5,
    marginBottom: 12,
  },
  statsRow: {
    display: 'flex',
    gap: 20,
    color: '#888',
    fontSize: 13,
    marginBottom: 10,
  },
  tagsRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  expeditionsBox: {
    marginBottom: 12,
    padding: '10px 12px',
    borderRadius: 8,
    background: '#faf7fd',
    border: '1px solid rgba(123, 31, 162, 0.2)',
  },
  expeditionsTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#7b1fa2',
    marginBottom: 6,
  },
  expeditionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    fontSize: 12.5,
    padding: '4px 0',
  },
  postcardBox: {
    marginTop: 8,
    padding: '10px 12px',
    background: '#fffdf5',
    border: '1px solid #e8dfc0',
    borderRadius: 8,
    fontSize: 13,
    color: '#3a2f1d',
    lineHeight: 1.5,
  },
  queueStatusRow: {
    marginTop: 8,
    padding: '8px 12px',
    background: '#f4effc',
    border: '1px solid #ddd0f0',
    borderRadius: 8,
    fontSize: 13,
    color: '#5b3a8e',
  },
  requestBox: {
    marginTop: 8,
    padding: '10px 12px',
    background: '#f8f6fc',
    border: '1px solid #e0d8f0',
    borderRadius: 8,
    fontSize: 13,
  },
  requestInput: {
    padding: '6px 8px',
    borderRadius: 6,
    border: '1px solid #ccc',
    fontSize: 13,
    background: '#fff',
    color: '#111',
  },
  memoryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12.5,
    padding: '4px 0',
  },
  memoryContent: {
    flex: 1,
    minWidth: 0,
    color: '#555',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  expeditionPublish: {
    padding: '3px 10px',
    borderRadius: 6,
    border: '1px solid #bbb',
    background: '#fff',
    color: '#888',
    fontSize: 11.5,
    fontWeight: 600,
    cursor: 'pointer',
  },
  expeditionWatch: {
    padding: '3px 10px',
    borderRadius: 6,
    border: '1px solid #7b1fa2',
    background: '#fff',
    color: '#7b1fa2',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  tag: {
    padding: '3px 10px',
    borderRadius: 12,
    background: 'rgba(123, 31, 162, 0.1)',
    color: '#7b1fa2',
    fontSize: 12,
  },
  actionsRow: {
    display: 'flex',
    gap: 10,
  },
  actionButton: {
    padding: '8px 14px',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#333',
    fontSize: 13,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  launchButton: {
    width: '100%',
    padding: '11px 16px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#555',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s',
    letterSpacing: 0.3,
    marginBottom: 10,
  },
  detailsBox: {
    marginTop: 14,
    padding: 14,
    borderRadius: 8,
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
  },
  detailLabel: {
    display: 'block',
    color: '#888',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  apiKeyCode: {
    display: 'block',
    padding: '8px 10px',
    background: '#fafafa',
    borderRadius: 4,
    fontFamily: 'monospace',
    fontSize: 12,
    color: '#e65100',
    border: '1px solid #e0e0e0',
    wordBreak: 'break-all',
    userSelect: 'all',
  },

  // --- Captured section ---
  capturedSection: {
    marginTop: 64,
    paddingTop: 48,
    borderTop: '1px solid #e0e0e0',
  },
  capturedHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: '#111',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: 0,
  },
  captureIcon: {
    fontSize: 22,
  },
  capturedSubtitle: {
    color: '#888',
    fontSize: 13,
  },
  // ⬇️ 09-29 memory download: the add-by-key entry
  addKeyRow: {
    marginTop: -12,
    marginBottom: 20,
  },
  addKeyLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: '#4a6cf7',
    fontSize: 13.5,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  addKeyForm: {
    maxWidth: 520,
    padding: '12px 14px',
    border: '1px solid #e2e2e2',
    borderRadius: 10,
    background: '#fafafa',
  },
  addKeyLabel: {
    display: 'block',
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 6,
    color: '#333',
  },
  addKeyInput: {
    flex: '1 1 220px',
    minWidth: 0,
    boxSizing: 'border-box',
    padding: '8px 10px',
    border: '1px solid #ccc',
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'monospace',
  },
  addKeyButton: {
    padding: '8px 16px',
    borderRadius: 8,
    border: 'none',
    background: '#4a6cf7',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13.5,
    cursor: 'pointer',
  },
  addKeyHint: {
    marginTop: 8,
    fontSize: 12,
    color: '#777',
    lineHeight: 1.45,
  },
  addKeyError: {
    marginTop: 8,
    fontSize: 13,
    color: '#b3261e',
  },
  addKeyOk: {
    marginTop: 8,
    fontSize: 13,
    color: '#1e7a3c',
  },
  emptyCapturedState: {
    textAlign: 'center',
    padding: '32px 20px',
    background: '#fafafa',
    border: '1px dashed #ddd',
    borderRadius: 12,
  },
  inlineCaptureChip: {
    display: 'inline-block',
    margin: '0 6px',
    padding: '2px 8px',
    borderRadius: 10,
    background: 'linear-gradient(90deg, #f7b733, #fc4a1a)',
    color: '#fff',
    fontWeight: 600,
    fontSize: 12,
  },
  browseButton: {
    padding: '12px 24px',
    borderRadius: 10,
    border: '1px solid #0a0a0a',
    background: '#fff',
    color: '#0a0a0a',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  capturedCard: {
    background: '#fff',
    border: '1px solid #f0c060',
    borderLeft: '4px solid #f7b733',
    borderRadius: 12,
    padding: 20,
    boxShadow: '0 2px 8px rgba(247, 183, 51, 0.15)',
  },
  releaseButton: {
    color: '#c62828',
    borderColor: '#f5b5b1',
  },
};

// Hook: store API key after registration so dashboard can find it
export const saveAgentApiKey = (agentId, apiKey) => {
  storeKey(agentId, apiKey);
};

export default AgentDashboardPage;
